import { PropertySortFieldEnum, PropertyStatus, PropertyType, SortOrder } from '../types';
import {
    ColumnFiltersState,
    SortingState,
    VisibilityState,
} from '@tanstack/react-table';
import { useState } from 'react';

export const usePropertiesTableData = () => {
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [searchQuery, setSearchQuery] = useState("");

    // Отримуємо значення фільтрів
    const typeFilter = columnFilters.find(filter => filter.id === 'type')?.value as PropertyType | "ALL" || "ALL";
    const statusFilter = columnFilters.find(filter => filter.id === 'status')?.value as PropertyStatus | "ALL" || "ALL";
    const minPrice = columnFilters.find(filter => filter.id === 'minPrice')?.value as number | undefined;
    const maxPrice = columnFilters.find(filter => filter.id === 'maxPrice')?.value as number | undefined;
    const city = columnFilters.find(filter => filter.id === 'city')?.value as string | undefined;
    const minArea = columnFilters.find(filter => filter.id === 'minArea')?.value as number | undefined;
    const maxArea = columnFilters.find(filter => filter.id === 'maxArea')?.value as number | undefined;
    const minRooms = columnFilters.find(filter => filter.id === 'minRooms')?.value as number | undefined;
    const maxRooms = columnFilters.find(filter => filter.id === 'maxRooms')?.value as number | undefined;
    const features = columnFilters.find(filter => filter.id === 'features')?.value as string[] | undefined;

    // Визначаємо поле та напрямок сортування
    const sortField = sorting[0]?.id as PropertySortFieldEnum | undefined;
    const sortOrder = sorting[0]?.desc ? SortOrder.DESC : sorting[0] ? SortOrder.ASC : undefined;

    function handleSearch(value: string) {
        setSearchQuery(value);
        setPage(0);
    }

    function handleTypeChange(value: PropertyType | "ALL") {
        setColumnFilters(prev => {
            const filtered = prev.filter(filter => filter.id !== 'type');
            if (value !== "ALL") {
                filtered.push({ id: 'type', value });
            }
            return filtered;
        });
        setPage(0);
    }

    function handleStatusChange(value: PropertyStatus | "ALL") {
        setColumnFilters(prev => {
            const filtered = prev.filter(filter => filter.id !== 'status');
            if (value !== "ALL") {
                filtered.push({ id: 'status', value });
            }
            return filtered;
        });
        setPage(0);
    }

    function handlePriceChange(min?: number, max?: number) {
        setColumnFilters(prev => {
            const filtered = prev.filter(filter => !['minPrice', 'maxPrice'].includes(filter.id));
            if (min !== undefined) filtered.push({ id: 'minPrice', value: min });
            if (max !== undefined) filtered.push({ id: 'maxPrice', value: max });
            return filtered;
        });
        setPage(0);
    }

    function handleAreaChange(min?: number, max?: number) {
        setColumnFilters(prev => {
            const filtered = prev.filter(filter => !['minArea', 'maxArea'].includes(filter.id));
            if (min !== undefined) filtered.push({ id: 'minArea', value: min });
            if (max !== undefined) filtered.push({ id: 'maxArea', value: max });
            return filtered;
        });
        setPage(0);
    }

    function handleRoomsChange(min?: number, max?: number) {
        setColumnFilters(prev => {
            const filtered = prev.filter(filter => !['minRooms', 'maxRooms'].includes(filter.id));
            if (min !== undefined) filtered.push({ id: 'minRooms', value: min });
            if (max !== undefined) filtered.push({ id: 'maxRooms', value: max });
            return filtered;
        });
        setPage(0);
    }

    function handleCityChange(value?: string) {
        setColumnFilters(prev => {
            const filtered = prev.filter(filter => filter.id !== 'city');
            if (value) filtered.push({ id: 'city', value });
            return filtered;
        });
        setPage(0);
    }

    function handleFeaturesChange(value?: string[]) {
        setColumnFilters(prev => {
            const filtered = prev.filter(filter => filter.id !== 'features');
            if (value?.length) filtered.push({ id: 'features', value });
            return filtered;
        });
        setPage(0);
    }

    return {
        // Стан пагінації
        page,
        setPage,
        size,
        setSize,
        
        // Стан таблиці
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        columnVisibility,
        setColumnVisibility,
        
        // Стан фільтрів
        searchQuery,
        setSearchQuery: handleSearch,
        typeFilter,
        statusFilter,
        minPrice,
        maxPrice,
        city,
        minArea,
        maxArea,
        minRooms,
        maxRooms,
        features,
        
        // Функції для фільтрів
        handleTypeChange,
        handleStatusChange,
        handlePriceChange,
        handleAreaChange,
        handleRoomsChange,
        handleCityChange,
        handleFeaturesChange,
        
        // Дані для сортування
        sortField,
        sortOrder,
    };
}; 